import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import api from '../api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem('pbc_user');
    return stored ? JSON.parse(stored) : null;
  });

  // The signed-in coach's own photo, in one place.
  //
  // Two screens need it — the top bar and the profile page — and they have to
  // agree: change your photo and the little one by your name changes with it,
  // straight away. Holding it here rather than in each page is what makes that
  // happen without one of them going stale.
  //
  // It is NOT put in localStorage. Photos change, localStorage does not expire,
  // and a picture cached in the browser after somebody deleted it is exactly
  // the kind of thing you cannot explain to a coach who asked for it gone.
  const [myPhoto, setMyPhoto] = useState({ has_photo: false, small: null, large: null, updated_at: null });

  // Check with the server who we still are, once, each time the app loads.
  //
  // The line above reads the signed-in person out of localStorage, which is a
  // snapshot taken when they logged in and can be up to thirty days old. The
  // server already refuses to act on a stale role — an admin demoted to coach
  // is a coach on their very next request — but nothing had told the SCREENS
  // that, so the demoted admin would still be looking at a Coaches tab and a
  // Bills tab where every click came back refused. This asks once and puts the
  // menu right.
  //
  // It costs one small request on page load. A 401 (deactivated account, or a
  // password changed elsewhere) needs no handling here: the interceptor in
  // api.js already signs them out and says why.
  useEffect(() => {
    if (!localStorage.getItem('pbc_token')) return;
    let cancelled = false;
    api.get('/api/auth/me')
      .then(res => {
        const d = res.data;
        if (cancelled || !d || !d.id) return;
        setUser(prev => {
          if (prev
            && prev.role === d.role
            && prev.name === d.name
            && prev.email === d.email
            && prev.hourly_rate === d.hourly_rate
            // Included so a password an ADMIN resets while this tab is open
            // takes effect here too. Leave it out and the tab would carry on as
            // if nothing had happened until the next full reload.
            && prev.must_change_password === (d.must_change_password === true)) return prev;
          const fresh = {
            id: d.id, name: d.name, email: d.email, role: d.role, hourly_rate: d.hourly_rate,
            must_change_password: d.must_change_password === true
          };
          localStorage.setItem('pbc_user', JSON.stringify(fresh));
          return fresh;
        });
      })
      .catch(() => { /* Signed out, or offline. Either way, nothing to do here. */ });
    return () => { cancelled = true; };
    // Deliberately once, on mount. Not tied to `user`, or it would chase itself.
  }, []);

  const refreshMyPhoto = useCallback(async () => {
    if (!user || user.role !== 'coach') {
      setMyPhoto({ has_photo: false, small: null, large: null, updated_at: null });
      return;
    }
    try {
      const res = await api.get('/api/profile/photo');
      setMyPhoto(res.data);
    } catch {
      // No photo on screen is fine. A page that will not load is not.
      setMyPhoto({ has_photo: false, small: null, large: null, updated_at: null });
    }
  }, [user]);

  useEffect(() => { refreshMyPhoto(); }, [refreshMyPhoto]);

  const login = async (email, password) => {
    const res = await api.post('/api/auth/login', { email, password });
    const { token, user } = res.data;
    localStorage.setItem('pbc_token', token);
    localStorage.setItem('pbc_user', JSON.stringify(user));
    setUser(user);
    return user;
  };

  // Swap in a token the server has just handed back.
  //
  // Used by the change-password form. Changing your password invalidates every
  // token issued before it, INCLUDING the one this browser is holding, so the
  // server issues a replacement and this puts it away. Miss this and the coach
  // is signed out of the device they just used, which reads as the change
  // having failed.
  //
  // Only the token moves. Who you are has not changed, so `user` is left alone.
  const applyNewToken = (token) => {
    if (token) localStorage.setItem('pbc_token', token);
  };

  // Somebody has just set a new password from an emailed reset link, and the
  // server handed back a token for them. They are signed in, exactly as if they
  // had used the login form, which is what the screen's wording promises.
  const signInWithToken = (token, freshUser) => {
    localStorage.setItem('pbc_token', token);
    localStorage.setItem('pbc_user', JSON.stringify(freshUser));
    setUser(freshUser);
  };

  // They were on a password somebody else chose for them and have just chosen
  // their own, so the portal stops holding them on the change-password form.
  //
  // Done here rather than by re-asking the server because the server has already
  // answered: the route that clears the column is the same request that just
  // succeeded. One less round trip, and no window where the screen still thinks
  // they are locked.
  const clearMustChangePassword = () => {
    setUser(prev => {
      if (!prev || !prev.must_change_password) return prev;
      const fresh = { ...prev, must_change_password: false };
      localStorage.setItem('pbc_user', JSON.stringify(fresh));
      return fresh;
    });
  };

  const logout = () => {
    localStorage.removeItem('pbc_token');
    localStorage.removeItem('pbc_user');
    setUser(null);
    // Drop the photo with the session. The next person to log in on this
    // laptop must not see the last one's face in the top bar.
    setMyPhoto({ has_photo: false, small: null, large: null, updated_at: null });
  };

  return (
    <AuthContext.Provider value={{
      user, login, logout, applyNewToken, signInWithToken, clearMustChangePassword,
      myPhoto, setMyPhoto, refreshMyPhoto
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
